import type { LegacyLocationQuery } from './Location'
import type { NativePluginEventListenerHandler, NativePluginEventListenerRemover, NativePluginEventName } from './NativePlugin'
import type { DrugProduct, Prescription } from './Prescription'
import type { DrugIcd, UnfilteredIcd } from './Icd'
import type { SecondaryCode } from './SecondaryCode'
import type { ConceptStatusValidityByCid } from './ConceptStatusValidity'
import type { CodeNext } from './CodeNext'

// Form -> App Relay
// This file contains all types that the Form (formrender module) uses to
// access functionality provided by the App (frontend module).
// It should only be changed very carefully so as not to break compatibility
// with all existing compiled formrender modules for historic forms.

// Form Render -> App
export interface FormAppRelayInterface {
	updateRenderState: (renderState: Partial<FormUpdatableRenderState>) => void
	/**
	 * @deprecated: 20251120
	 * see comments in @fusion-advantage/backcompat Location.ts
	 */
	updateUserLocation: () => Promise<updateUserLocationResult>
	downloadFile: (args: downloadFileArgs) => Promise<downloadFileResult|null>
	uploadFile: (args: uploadFileArgs) => Promise<uploadFileResult|null>
	addNativePluginListener: (args: addNativePluginListenerArgs) => addNativePluginListenerResult
	showRxScanner: (args: showRxScannerArgs) => void
	showBarcodeScanner: (args: showBarcodeScannerArgs) => void
	queryDrugProducts: () => Promise<queryDrugProductsResult>
	/**
	 * @deprecated: 20260730
	 * replaced with selectDrugIcdCodes
	 */
	queryIcdCodes: () => Promise<selectDrugIcdCodesResult>
	/**
	 * @deprecated: 20260730
	 * replaced with selectDrugIcdCodes
	 */
	queryIcdCodesByProductId: (productId: number) => Promise<selectDrugIcdCodesResult>
	// for info on distinction between Drug and Unfiltered ICD
	// see comments in Icd.ts
	selectDrugIcdCodes: (args: selectDrugIcdCodesArgs) => Promise<selectDrugIcdCodesResult>
	queryMaterialStatus: (args: queryMaterialStatusArgs) => Promise<queryMaterialStatusResult>
	// for info on distinction between Drug and Unfiltered ICD
	// see comments in Icd.ts
	queryUnfilteredIcds: (args: queryUnfilteredIcdsArgs) => Promise<queryUnfilteredIcdsResult>
	querySecondaryCodes: (args: querySecondaryCodesArgs) => Promise<querySecondaryCodesResult|null>
	exitForm: () => void
	saveFormDraft: (args: saveFormDraftArgs) => Promise<void>
	submitForm: (args: submitFormArgs) => Promise<void>
}

export enum RenderMedia {
	Screen = 'screen',
	Print = 'print',
}

export interface FormUpdatableRenderState {
	t1NavAvailableFor: Record<string, boolean>
	media: RenderMedia
}

/**
 * @deprecated: 20251120
 * see comments in @fusion-advantage/backcompat Location.ts
 */
interface updateUserLocationResult {
	locationQuery: LegacyLocationQuery
}

interface downloadFileArgs {
	BlobName: string
}

interface downloadFileResult {
	SasUrl: string
}

interface uploadFileArgs {
	File: File
}

interface uploadFileResult {
	BlobName: string
}

interface addNativePluginListenerArgs {
	name: NativePluginEventName
	handler: NativePluginEventListenerHandler
}

interface addNativePluginListenerResult {
	remover: NativePluginEventListenerRemover
}

export enum RxScannerVersion {
	V1 = "v1",
	V2Simplified = "v2simplified",
}

export interface showRxScannerArgs {
	// forms using renderer >= 27
    // use the "simplified" version of the native drug scanner
	version?: RxScannerVersion
	models: Prescription[]
	assessmentSupportedIcd10s: string[]
	priorIcd10s: string[]
}

export enum BarcodeType {
	QR = 'qr',
	Code128 = 'code128',
}

export interface showBarcodeScannerArgs {
	barcodeType: BarcodeType
}

interface queryDrugProductsResult {
	products: DrugProduct[]
}

export interface selectDrugIcdCodesArgs {
	productId?: number
}

interface selectDrugIcdCodesResult {
	icdCodes: DrugIcd[]
}

export enum MaterialArea {
	Labs = 'Labs',
}

export interface MaterialStatusQuery {
	area: MaterialArea
	id: string
	type: string
}

interface queryMaterialStatusArgs {
	query: MaterialStatusQuery
}

export enum MaterialStatus {
	// database says the material is used
	Used = 'Used',
	// database says the material is expired
	Expired = 'Expired',
	// database say the material is explicitly available
	Available = 'Available',
	// database doesn't know about the material
	Unknown = 'Unknown',
	// database says material exists but is different Type within same Area
	WrongType = 'WrongType',
}

interface queryMaterialStatusResult {
	status: MaterialStatus
}

interface queryUnfilteredIcdsArgs {
	icdCodeQuery: string
}

interface queryUnfilteredIcdsResult {
	icdCodes: UnfilteredIcd[]
}

interface querySecondaryCodesArgs {
	// using this simpler type definition to avoid porting the entire
	// DiagnosisReviewItem model and its dependencies into backcompat.
	// it's unclear what small part of the DiagnosisReviewItem models that
	// Dan has been using in his proc to perform this, and I don't think
	// anyone really cares if it's backward compatible forever.
	// if we want to lock it down later, we can provide a simpler type that
	// provides just the part of the model Dan's proc actually cares about.
	models: any[]
}

interface querySecondaryCodesResult {
	secondaryCodes: SecondaryCode[]
}

interface saveFormDraftArgs {
	closeOnSuccess: boolean
}

interface submitFormArgs {
	codeNext: CodeNext
	validityByCid: ConceptStatusValidityByCid
}
